import { useState, useMemo } from 'react';
import * as R from 'ramda';
import deepEqual from 'deep-equal';
import {
	preProcessFields,
	postProcessFields,
	createJoiValidationSchema,
	validateData
} from '../../../../lib/utils/utils';

// Form state engine. Replaces the former recompose `withForm` HOC; injected
// props are now returned from this hook. All setters merge into state, matching
// the previous withStateCreators merge semantics.
const useForm = ({ joiValidationSchema, editing, hideCancel, schema } = {}) => {
	const [state, setState] = useState(() => {
		const finalSchema = schema || joiValidationSchema;
		return {
			touched: false,
			data: {},
			origData: {},
			hideCancel: hideCancel !== undefined ? hideCancel : false,
			editing: editing !== undefined ? editing : false,
			showPassword: false,
			validationSchema: createJoiValidationSchema(finalSchema),
			preProcessData: preProcessFields(finalSchema),
			postProcessData: postProcessFields(finalSchema)
		};
	});

	const updateData = newData =>
		setState(s => {
			const nextData = R.mergeRight(s.data, newData);
			return {
				...s,
				data: nextData,
				touched: !deepEqual(nextData, s.origData)
			};
		});
	const updateInitData = data =>
		setState(s => ({ ...s, data, origData: data }));
	const updateEditing = editing => setState(s => ({ ...s, editing }));
	const updateShowPassword = showPassword =>
		setState(s => ({ ...s, showPassword }));
	const updateAll = all => setState(s => ({ ...s, ...all }));

	const onShowPassword = () => updateShowPassword(!state.showPassword);
	const onSave = () =>
		updateAll({
			editing: false,
			origData: state.data,
			data: state.data,
			touched: false
		});
	const onCancel = () =>
		updateAll({ editing: false, data: state.origData, touched: false });
	const onEdit = () => updateEditing(true);
	const onUpdateData = e =>
		R.compose(
			updateData,
			R.objOf(e.target.id || e.target.name)
		)(e.target.value);

	const validation = useMemo(
		() =>
			validateData({
				data: state.data,
				validationSchema: state.validationSchema,
				options: {
					abortEarly: false
				}
			}),
		[state.data, state.validationSchema]
	);

	return {
		...state,
		validation,
		onShowPassword,
		onSave,
		onCancel,
		onEdit,
		onUpdateData,
		updateData,
		updateInitData,
		updateEditing,
		updateShowPassword,
		updateAll
	};
};

export { useForm };
